/**
 * Which category hubs and DB categories are live right now.
 *
 * A category is live when it has active sounds. The retired-name list in
 * `category-taxonomy.ts` knows which shelves were folded away; this module
 * answers the same question from the data, so a shelf that empties later
 * (a curation sweep, a deactivation batch) disappears from every listing
 * without another code change.
 *
 * `counts` is `soundsApi.getCategoryCounts()`: active sounds keyed by DB name.
 * An empty record means the counts were unavailable, never "everything is
 * empty", so each helper then keeps what it was given and a failed query
 * cannot blank a listing.
 */

import {
  resolveBlogSoundCategory,
  slugToDbCategoryName,
  slugToDbCategoryNames,
  VELOCITY_SLUGS,
  getCategoryBySlug,
} from './category-data'
import { CATEGORY_MERGES, withoutRetiredCategories } from './category-taxonomy'
import { MERGED_CATEGORY_REDIRECTS } from './merged-category-redirects'

export type CategoryCounts = Readonly<Record<string, number>>

const countsAvailable = (counts: CategoryCounts) => Object.keys(counts).length > 0

/** `categories` rows with active sounds. Falls back to the retired-name list when counts are unavailable. */
export function withoutEmptyCategories<T extends { name: string }>(
  rows: readonly T[],
  counts: CategoryCounts,
): T[] {
  const current = withoutRetiredCategories(rows)
  if (!countsAvailable(counts)) return current
  return current.filter(row => (counts[row.name] ?? 0) > 0)
}

/** The DB categories a hub lists, keeping only the ones that hold active sounds. */
export function liveHubDbNames(slug: string, counts: CategoryCounts): string[] {
  const names = [slugToDbCategoryNames(slug)].flat()
  if (!countsAvailable(counts)) return names
  return names.filter(name => (counts[name] ?? 0) > 0)
}

/**
 * A hub worth linking to: not retired, and (for a DB-backed hub) holding at
 * least one active sound. Velocity hubs are ranked from another table.
 */
export function isLiveHub(slug: string, counts: CategoryCounts): boolean {
  if (Object.hasOwn(MERGED_CATEGORY_REDIRECTS, slug)) return false
  if (VELOCITY_SLUGS.has(slug)) return true
  if (!countsAvailable(counts)) return true
  return liveHubDbNames(slug, counts).length > 0
}

/** A hub's `relatedCategories` without itself, retired hubs, or empty hubs. */
export function liveRelatedSlugs(slug: string, counts: CategoryCounts): string[] {
  const related = getCategoryBySlug(slug)?.relatedCategories ?? []
  return related.filter(other => other !== slug && isLiveHub(other, counts))
}

/**
 * Where an emptied hub sends a visitor: the parent its DB category folds into
 * when that parent has a hub, otherwise the library.
 */
export function emptyHubDestination(slug: string): string {
  const parentDbName = CATEGORY_MERGES[slugToDbCategoryName(slug)]
  const parent = parentDbName ? resolveBlogSoundCategory(parentDbName) : undefined
  return parent?.kind === 'hub' && parent.slug !== slug
    ? `/sounds/category/${parent.slug}`
    : '/sounds'
}
